import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Info, Loader2, SlidersHorizontal, X } from 'lucide-react';
import SiteLayout from '../../site/components/SiteLayout';
import SearchWidget from './SearchWidget';
import ResultsFilters from './ResultsFilters';
import { useTravelSearch } from '../hooks/useTravelSearch';
import { DEFAULT_FILTERS, filterResults, getPriceBounds } from '../utils/resultFilters';
import { hasSearchQuery, SEARCH_PROMPTS } from '../utils/searchQuery';
import PhoneCta from './PhoneCta';

export default function SearchResultsLayout({
  type,
  title,
  summary,
  renderCard,
  bannerImage,
  searchParams: paramsProp,
}) {
  const [searchParams] = useSearchParams();
  const params = paramsProp || Object.fromEntries(searchParams.entries());
  const queryReady = useMemo(() => hasSearchQuery(type, searchParams), [type, searchParams]);

  const { loading, results, source, error, catalogInfo } = useTravelSearch(type, params, {
    enabled: queryReady,
  });

  const bounds = useMemo(() => getPriceBounds(results), [results]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      minPrice: bounds.min,
      maxPrice: Math.max(bounds.max, bounds.min + 100),
    }));
  }, [bounds.min, bounds.max]);

  useEffect(() => {
    if (!showFilters) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showFilters]);

  const filtered = useMemo(
    () => filterResults(type, results, filters),
    [type, results, filters]
  );

  const tabMap = {
    flights: 'flights',
    hotels: 'hotels',
    cars: 'cars',
    packages: 'packages',
    cruises: 'cruises',
  };

  const searchTitle = title.replace(/ Results?$/i, '');

  return (
    <SiteLayout>
      <section
        className="results-hero"
        style={bannerImage ? { '--results-banner': `url(${bannerImage})` } : undefined}
      >
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={18} /> Back to home
          </Link>
          <div className="results-hero__row">
            <div>
              <h1>{queryReady ? title : searchTitle}</h1>
              <p className="results-summary">
                {queryReady ? summary(searchParams) : SEARCH_PROMPTS[type]}
              </p>
            </div>
            {queryReady && (
              <span className={`data-badge data-badge--${source}`}>
                {type === 'flights' ? 'Sample fares' : 'Sample availability'}
              </span>
            )}
          </div>
          {queryReady && catalogInfo && <p className="results-catalog-info">{catalogInfo}</p>}
          {queryReady && error && (
            <p className="results-api-notice" role="status">
              {error}
            </p>
          )}
        </div>
      </section>

      {!queryReady ? (
        <section className="results-search-prompt section--soft">
          <div className="container results-search-prompt__inner">
            <SearchWidget defaultTab={tabMap[type] || 'flights'} variant="hero" />
          </div>
        </section>
      ) : (
        <>
          <div className="container results-page">
            <aside className={`results-filters ${showFilters ? 'open' : ''}`}>
              <div className="results-filters__head">
                <h3>
                  <SlidersHorizontal size={18} /> Filters
                </h3>
                <button
                  type="button"
                  className="results-filters__close"
                  onClick={() => setShowFilters(false)}
                  aria-label="Close filters"
                >
                  <X size={22} aria-hidden />
                </button>
              </div>
              <ResultsFilters
                type={type}
                filters={filters}
                onChange={setFilters}
                results={results}
                onReset={setFilters}
              />
              <button
                type="button"
                className="btn-secondary mobile-only filters-apply"
                onClick={() => setShowFilters(false)}
              >
                Apply filters
              </button>
              <PhoneCta
                variant="compact"
                className="phone-cta--sidebar"
                title="Need help with this search?"
                subtitle="Call a FlyNodex advisor (not an airline) for live prices."
              />
            </aside>

            <div className="results-main">
              <div className="results-toolbar">
                {loading ? (
                  <p className="loading-text">
                    <Loader2 size={18} className="spin" /> Loading sample results…
                  </p>
                ) : (
                  <p>
                    <strong>{filtered.length}</strong> of {results.length} results
                  </p>
                )}
                <button
                  type="button"
                  className="btn-secondary mobile-only"
                  onClick={() => setShowFilters(true)}
                >
                  Filters
                </button>
              </div>

              <p className="fx-sample-notice" role="note">
                <Info size={18} aria-hidden />
                <span>
                  These are sample results to help you plan. They are not live prices or availability, and provider
                  names are not shown. Request any option and a FlyNodex advisor will confirm the real price, rules and
                  service fee before anything is booked.
                </span>
              </p>
              <div className="results-list">
                {loading ? (
                  <div className="results-skeleton">
                    {[1, 2, 3, 4].map((n) => (
                      <div key={n} className="skeleton-card" />
                    ))}
                  </div>
                ) : filtered.length === 0 ? (
                  <p className="no-results">
                    No results match your filters. Try resetting filters or changing dates.
                  </p>
                ) : (
                  filtered.map((item) => renderCard(item, searchParams))
                )}
              </div>
              <PhoneCta
                variant="compact"
                className="phone-cta--results"
                title="Found an option you like?"
                subtitle="Call a FlyNodex advisor for the live price. Nothing is booked without your approval."
              />
            </div>
          </div>

          <section className="results-refine section--soft">
            <div className="container">
              <h2 className="refine-title">Refine your search</h2>
              <SearchWidget defaultTab={tabMap[type] || 'flights'} />
            </div>
          </section>
        </>
      )}

    </SiteLayout>
  );
}
